/**
 * Mountain painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mountainPaintingIdeasPack: ToilePack = {
    slug: 'mountain-painting-ideas',
    category: 'craft',
    name: 'Mountain painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'lake', label: 'Mirror lake', subject: 'a canvas of snow peaks reflected in a still turquoise lake'},
        {key: 'ridges', label: 'Blue ridges', subject: 'a canvas of layered blue mountain ridges fading into a dusk sky'},
        {key: 'cabin', label: 'Starlit cabin', subject: 'a canvas of a small cabin at the foot of a pine slope under a starry sky'},
    ],
    styles: [
        {key: 'layered', label: 'Flat layers', style: 'easy beginner acrylic in flat layered shapes stepping from dark to light', palette: 'graded blues and greens in clean bands', framing: 'The canvas on a wooden easel seen straight on, a plain pale wall behind'},
        {key: 'knife', label: 'Palette knife', style: 'thick palette-knife impasto with knife-cut peaks and ridged snow', palette: 'cool blues and whites laid on in raised strokes', framing: 'The canvas leaning against a white wall on a light wooden floor'},
        {key: 'wetonwet', label: 'Wet on wet', style: 'wet-on-wet oil in the quick television-landscape manner, a soft blended sky and crisp knife-cut highlights', palette: 'natural greens, blues and white blended softly', framing: 'The canvas lying flat on a wooden work table seen from directly above, brushes and a paint palette beside it'},
    ],
    directions: ['autumn colors', 'sunrise', 'with a river'],
    directionHint: 'try: autumn colors',
    seo: {
        title: 'Mountain Painting Ideas: Lake, Ridges, Starlit Cabin',
        metaDescription: 'Nine mountain canvases: snow peaks over a still lake, blue ridges at dusk and a cabin under the stars, in three beginner styles.',
        h1: 'Mountain painting ideas: mirror lake, blue ridges, starlit cabin',
        tagline: 'Three mountain subjects, each painted as flat layers, palette knife or wet on wet.',
        intro: "Mountains suit a beginner's canvas: layers of color, a reflection and a sky. These nine show snow peaks reflected in a still turquoise lake, layered blue ridges fading into a dusk sky, and a small cabin under a pine slope at night. Each is painted as flat layers, with a thick palette-knife finish, or wet on wet. Type a different twist, such as autumn colors or a river, and the same three styles carry it.",
        useCases: [
            'a first landscape painting',
            'a reference for a painting class',
            'a calm wall print for a bedroom',
            'a canvas for a cabin-style living room',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three mountain scenes in three styles: snow peaks in a still lake, blue ridges at dusk, and a cabin under a pine slope at night.'},
            {q: 'What is the difference between the three styles?', a: 'Flat layers uses clean bands of graded blues and greens. Palette knife has thick raised strokes for peaks and snow. Wet on wet has a soft blended sky with crisp knife-cut highlights.'},
            {q: 'Can I change the subject?', a: 'Yes. Type a direction such as autumn colors, sunrise or with a river, and the same three styles apply. The nine samples are the ones shown.'},
            {q: 'What shape are the pictures?', a: 'Each picture is tall, in portrait format.'},
        ],
    },
}
